import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { DelayByShift } from "../models/DelayByShift";
import { DelaysSummary } from "../models/DelaysSummary";
const moment = getMomentTz();

// [DEL].[GetDelays] handles both shift mode (ShiftId) and period mode
// (StartDate/EndDate).
export const getDelays = async ({
	shiftId = null,
	startDate = null,
	endDate = null,
	delayAreaAssetId = null,
	crew = null,
	delayCategoryId = null,
	delayCodeId = null,
	productTypeId = null,
	productSizeId = null,
}: {
	shiftId?: number | null;
	startDate?: string | null;
	endDate?: string | null;
	delayAreaAssetId?: number | null;
	crew?: string | null;
	delayCategoryId?: number | null;
	delayCodeId?: number | null;
	productTypeId?: number | null;
	productSizeId?: number | null;
}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	if (shiftId) parameters.push({ name: "ShiftId", value: shiftId });
	if (startDate) parameters.push({ name: "StartDate", value: startDate });
	if (endDate) parameters.push({ name: "EndDate", value: endDate });
	if (delayAreaAssetId)
		parameters.push({ name: "DelayAreaAssetId", value: delayAreaAssetId });
	if (crew) parameters.push({ name: "Crew", value: crew });
	if (delayCategoryId)
		parameters.push({ name: "DelayCategoryId", value: delayCategoryId });
	if (delayCodeId) parameters.push({ name: "DelayCodeId", value: delayCodeId });
	if (productTypeId)
		parameters.push({ name: "ProductTypeId", value: productTypeId });
	if (productSizeId)
		parameters.push({ name: "ProductSizeId", value: productSizeId });

	const resp = await apiService.callV2("[DEL].[GetDelays]", parameters);
	if (resp.ok) {
		let rows = get(resp, "data.tables[0].rows", []);

		rows = rows.map((delay: DelayByShift) => ({
			...delay,
			Start: delay.Start ? moment.utc(delay["Start"]).toDate() : null,
			End: delay.End ? moment.utc(delay["End"]).toDate() : null,
		}));

		const summaryRow = get(resp, "data.tables[1].rows[0]", null);
		const summary: DelaysSummary = summaryRow
			? {
					count: summaryRow.Count ?? 0,
					durationSeconds: summaryRow.DurationSeconds ?? 0,
				}
			: { count: 0, durationSeconds: 0 };

		return { rows: rows as DelayByShift[], summary };
	} else {
		throw new Error(resp.message || "Error fetching delays");
	}
};

export const getDelay = async ({ delayId }: { delayId: number | null }) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "DelayId", value: delayId }];

	const resp = await apiService.callV2("[DEL].[GetDelay]", parameters);
	if (resp.ok) {
		let rows = get(resp, "data.tables[0].rows", []);

		rows = rows.map((delay: DelayByShift) => ({
			...delay,
			Start: delay.Start ? moment.utc(delay["Start"]).toDate() : null,
			End: delay.End ? moment.utc(delay["End"]).toDate() : null,
		}));

		return rows as DelayByShift[];
	} else {
		throw new Error(resp.message || "Error fetching delay");
	}
};

export const upsertDelay = async (
	DelayId: number | null,
	AssetId: number | null,
	Start: Date | null,
	End: Date | null,
	DelayCodeId: number | null,
	DelayCategoryId: number | null,
	Comments: string | null,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "DelayId", value: DelayId },
		{ name: "AssetId", value: AssetId },
		{
			name: "Start",
			value: Start ? Start.toISOString() : null,
		},
		{
			name: "End",
			value: End ? End.toISOString() : null,
		},
		{ name: "DelayCodeId", value: DelayCodeId },
		{ name: "DelayCategoryId", value: DelayCategoryId },
		{ name: "Comments", value: Comments },
	];

	return await apiService.callV2("[DEL].[UpsertManualDelay]", parameters);
};

export const justifyDelay = async (
	delayId: number,
	delayCodeId: number | null,
	delayCategoryId: number | null,
	comments: string | null,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "DelayId", value: delayId },
		{ name: "DelayCodeId", value: delayCodeId === 0 ? null : delayCodeId },
		{ name: "DelayCategoryId", value: delayCategoryId },
		{ name: "Comments", value: comments },
	];

	return await apiService.callV2("[DEL].[JustifyDelay]", parameters);
};

export const deleteDelay = async (delayId: number | null) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "DelayId", value: delayId }];

	return await apiService.callV2("[DEL].[DeleteManualDelay]", parameters);
};

export const splitDelay = async (
	delay1: DelayByShift,
	delay2: DelayByShift,
) => {
	const apiService = new MESApiService();
	const splitTime = delay1.End?.toISOString();
	const parameters: spParameter[] = [
		{ name: "DelayId", value: delay1.DelayId },
		{ name: "SplitTime", value: splitTime! },
		{ name: "DelayCodeId1", value: delay1.DelayCodeId },
		{ name: "DelayCodeId2", value: delay2.DelayCodeId },
		{ name: "DelayCategoryId1", value: delay1.DelayCategoryId },
		{ name: "DelayCategoryId2", value: delay2.DelayCategoryId },
		{ name: "Comments1", value: delay1.Comments },
		{ name: "Comments2", value: delay2.Comments },
	];
	return await apiService.callV2("[DEL].[SplitDelay]", parameters);
};
